import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { CurrentRespondentScreenDetails } from "./currentRespondentScreenDetails";
import { RespondentSurveySettings, RespondentSurveySettingsDTO } from "./respondentSurveySettings";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class SwitchLanguageRPC implements
  LeoRPC<SwitchLanguageRPC.Request, SwitchLanguageRPC.Response, SwitchLanguageRPC.Errors.Errors>
{
  abstract execute(
    request: SwitchLanguageRPC.Request
  ): Promise<LeoRPCResult<SwitchLanguageRPC.Response, SwitchLanguageRPC.Errors.Errors>>;
}

export namespace SwitchLanguageRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    languageId: string;
    surveyLinkId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSwitchLanguageRPCRequest: boolean = true;
  
    readonly languageId: LeoUUID;
    readonly surveyLinkId: LeoUUID;
  
  
    constructor(
    languageId: LeoUUID, 
    surveyLinkId: LeoUUID
    ) {
      this.languageId = languageId;
      this.surveyLinkId = surveyLinkId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const languageId = getLeoUuid(dto, "languageId", InvalidRequestLanguageIdError);
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      return new Request(
        languageId,
        surveyLinkId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          languageId: this.languageId.toDTO(),
          surveyLinkId: this.surveyLinkId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `SwitchLanguageRPC(languageId='${this.languageId}',surveyLinkId='${this.surveyLinkId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    currentRespondentScreenDetails: CurrentRespondentScreenDetails.CurrentRespondentScreenDetailsDTO;
    respondentSurveySettings: RespondentSurveySettingsDTO;
    submitConfirmationButtonText?: SurveyLocalizedTextDTO | null;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isSwitchLanguageResponse: boolean = true;
  
    readonly currentRespondentScreenDetails: CurrentRespondentScreenDetails.CurrentRespondentScreenDetails;
    readonly respondentSurveySettings: RespondentSurveySettings;
    readonly submitConfirmationButtonText: SurveyLocalizedText | null;
  
  
    constructor(
    currentRespondentScreenDetails: CurrentRespondentScreenDetails.CurrentRespondentScreenDetails, 
    respondentSurveySettings: RespondentSurveySettings, 
    submitConfirmationButtonText: SurveyLocalizedText | null = null
    ) {
      this.currentRespondentScreenDetails = currentRespondentScreenDetails;
      this.respondentSurveySettings = respondentSurveySettings;
      this.submitConfirmationButtonText = submitConfirmationButtonText;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const currentRespondentScreenDetails = CurrentRespondentScreenDetails.fromDTO(getObject(dto, "currentRespondentScreenDetails", InvalidResponseCurrentRespondentScreenDetailsError));
      const respondentSurveySettings = RespondentSurveySettings.fromDTO(getObject(dto, "respondentSurveySettings", InvalidResponseRespondentSurveySettingsError));
      let submitConfirmationButtonText = getOptionalObject(dto, "submitConfirmationButtonText");
      if(submitConfirmationButtonText !== undefined && submitConfirmationButtonText !== null) {
        submitConfirmationButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "submitConfirmationButtonText", InvalidResponseSubmitConfirmationButtonTextError));
      }
      return new Response(
        currentRespondentScreenDetails,
        respondentSurveySettings,
        submitConfirmationButtonText
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          currentRespondentScreenDetails: this.currentRespondentScreenDetails.toDTO(),
          respondentSurveySettings: this.respondentSurveySettings.toDTO(),
      };
      if (this.submitConfirmationButtonText !== undefined && this.submitConfirmationButtonText !== null){
        dto.submitConfirmationButtonText = this.submitConfirmationButtonText.toDTO();
      }
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `SwitchLanguage(currentRespondentScreenDetails='${this.currentRespondentScreenDetails}',respondentSurveySettings='${this.respondentSurveySettings}',submitConfirmationButtonText='${this.submitConfirmationButtonText}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    InvalidLanguageId = "INVALID_LANGUAGE_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `SwitchLanguageRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface InvalidLanguageIdDTO {
      code: string;
    }
    
    export class InvalidLanguageId extends LeoRPCError<RPCError> {
      readonly isInvalidLanguageId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidLanguageId;
        super(code);
      }
    
    
      toDTO(): InvalidLanguageIdDTO {
        const dto: InvalidLanguageIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidLanguageId.prototype.toString = function toString(): string {
      return `SwitchLanguageRPC.INVALID_LANGUAGE_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `SwitchLanguageRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    

    export type Errors = InvalidSurveyLinkId | InvalidLanguageId | SurveyIsClosed;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | InvalidLanguageIdDTO | SurveyIsClosedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.InvalidLanguageId:
          return new InvalidLanguageId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestLanguageIdError extends SwitchLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLanguageIdError";
  }
}

export class InvalidRequestSurveyLinkIdError extends SwitchLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidResponseCurrentRespondentScreenDetailsError extends SwitchLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseCurrentRespondentScreenDetailsError";
  }
}

export class InvalidResponseRespondentSurveySettingsError extends SwitchLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentSurveySettingsError";
  }
}

export class InvalidResponseSubmitConfirmationButtonTextError extends SwitchLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSubmitConfirmationButtonTextError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends SwitchLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseInvalidLanguageIdError extends SwitchLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidLanguageIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends SwitchLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
